import { useCallback, useEffect, useEffectEvent, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import type { MutationResponse } from '@/types/api/mutations';
import type { ClientCertificateListItem } from '@/types/api/queries';

import { Form } from '@/components/Form';
import { InputLabel } from '@/components/InputLabel';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { useAppForm } from '@/hooks';

import { cashRegisterQueries, useRefundReasonsListQuery } from '@/queries';

import { centsToMoneyFormat, DisplayToast } from '@/helpers';

import {
  createRefundClientCertificateFormSchema,
  type RefundClientCertificateFormValues,
} from '@/types/schemas';

import { createRefundClientCertificateData } from './utils';

const REGISTER_TARGET = 'cash_register';
const DEPOSIT_TARGET = 'deposit';

const FORM_ID = 'refund-client-certificate-form';

type RefundCertificateDialogProps = {
  isOpen: boolean;
  certificate: ClientCertificateListItem | null;
  cashRegistersUrl: string;
  refundReasonsUrl: string;
  onSuccess: () => void;
  onClose: () => void;
};

export const RefundCertificateDialog = (
  props: RefundCertificateDialogProps,
) => {
  const {
    isOpen,
    certificate,
    cashRegistersUrl,
    refundReasonsUrl,
    onSuccess: propOnSuccess,
    onClose,
  } = props;
  const { t } = useTranslation();

  const { data: cashRegistersData } = useQuery(
    cashRegisterQueries.accessibleOptions({
      url: cashRegistersUrl,
      isEnabled: isOpen,
    }),
  );
  const { data: refundReasonsData } = useRefundReasonsListQuery(
    isOpen ? refundReasonsUrl : '',
  );

  const cashRegisterOptions = useMemo(
    () =>
      (cashRegistersData?.data ?? []).map((register) => ({
        id: String(register.id),
        name: `${register.name} (${register.location_name})`,
      })),
    [cashRegistersData],
  );

  const refundReasonOptions = useMemo(
    () =>
      (refundReasonsData?.data ?? [])
        .filter((reason) => !reason.archived_at)
        .map((reason) => ({ id: String(reason.id), name: reason.name })),
    [refundReasonsData],
  );

  const defaultValues = useMemo<RefundClientCertificateFormValues>(
    () => ({
      refund_target: REGISTER_TARGET,
      cash_register_id: '',
      refund_reason_id: '',
      description: '',
    }),
    [],
  );

  const onSuccess = useCallback(
    (response: MutationResponse) => {
      if (response.flash_message) {
        DisplayToast.success(response.flash_message);
      }
      propOnSuccess();
      onClose();
    },
    [propOnSuccess, onClose],
  );

  const entityId = certificate?.actions.refund.replace(/^\//, '');

  const { form, formFields, onSubmit, isPending } = useAppForm({
    schema: createRefundClientCertificateFormSchema,
    defaultValues,
    transform: createRefundClientCertificateData,
    api_url: certificate?.url ?? '',
    entityId,
    redirectUrl: '',
    onSuccess,
  });

  const refundTarget = useWatch({
    control: form.control,
    name: 'refund_target',
  });

  const isDepositTarget = refundTarget === DEPOSIT_TARGET;

  const onRefundTargetChange = useCallback(
    (value: string) => {
      form.setValue(
        'refund_target',
        value as RefundClientCertificateFormValues['refund_target'],
      );
      if (value === DEPOSIT_TARGET) {
        form.setValue('cash_register_id', '');
        form.clearErrors('cash_register_id');
      }
    },
    [form],
  );

  const onFormReset = useEffectEvent(() => {
    form.reset(defaultValues);
  });

  useEffect(() => {
    if (isOpen) {
      onFormReset();
    }
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {`${t('Refund')}: ${certificate?.code ?? ''}`}
          </DialogTitle>
          <DialogDescription>
            {`${t('Only the un-redeemed remainder is refunded')}: ${centsToMoneyFormat(certificate?.liability_balance_cents ?? 0)}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <InputLabel label={t('Refund method')} />
          <RadioGroup
            value={refundTarget}
            onValueChange={onRefundTargetChange}
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem
                value={REGISTER_TARGET}
                id="certificate-refund-target-register"
              />
              <Label htmlFor="certificate-refund-target-register">
                {t('Pay out from cash register')}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem
                value={DEPOSIT_TARGET}
                id="certificate-refund-target-deposit"
              />
              <Label htmlFor="certificate-refund-target-deposit">
                {t("Credit to the buyer's deposit")}
              </Label>
            </div>
          </RadioGroup>
        </div>

        <Form
          form={form}
          id={FORM_ID}
          onSubmit={onSubmit}
        >
          {!isDepositTarget && (
            <formFields.Select
              name="cash_register_id"
              options={cashRegisterOptions}
              label={t('From cash register')}
              searchable
              isRequired
            />
          )}
          <formFields.Select
            name="refund_reason_id"
            options={refundReasonOptions}
            label={t('Refund reason')}
            searchable
            isRequired
          />
          <formFields.Textarea
            name="description"
            label={t('Description')}
          />
        </Form>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isPending}
          >
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="destructive"
            disabled={isPending}
          >
            {t('Refund')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
